// askAccess — who sees the Ask BigAgri entries (⌘K ask, the mic, /ask).
//
// The UI half of a rule the SERVER enforces: ask-bigagri and ask-tts refuse a
// FARM caller below manager tier (supabase/functions/_shared/askAccess.ts,
// requireTenantRole). This only hides what the server would refuse, so nobody
// is offered a button that answers "forbidden". askAccess.test.ts pins the two
// role lists together.
//
// Rulings (Jason, 2 Oct 2026): PACKHOUSE unchanged — any member of a tenant
// with the add-on. FARM pilot — farm managers and admins only.
import type { UserRole } from '@/types/database'
import type { TenantKind } from '@/lib/useActiveTenant'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { tenantRoleOf } from '@/modules/farm/blockEditGate'

/** The membership roles a FARM tenant's Ask is open to — the server's FARM_ASK_ROLES. */
export const FARM_ASK_ROLES: readonly UserRole[] = ['FARM_ADMIN', 'FARM_MANAGER', 'ADMIN', 'MANAGER', 'SUPER_ADMIN']

/** May a member with this tenant role, in a tenant of this kind, use Ask? */
export function askAvailableFor(kind: TenantKind | null | undefined, tenantRole: UserRole | null | undefined): boolean {
  if (kind === 'PACKHOUSE') return true
  if (kind === 'FARM') return !!tenantRole && FARM_ASK_ROLES.includes(tenantRole)
  return false
}

/**
 * The current session's answer. `settled` is false while the tenant or the
 * profile is still loading — show nothing yet rather than a refusal that
 * flips to the page a moment later. Fails closed: an unreadable profile on a
 * farm is "not allowed".
 */
export function useAskAccess(): { allowed: boolean; settled: boolean; kind: TenantKind | null } {
  const { data: tenant, isPending: tenantPending } = useActiveTenant()
  const { data: me, isPending: mePending } = useCurrentUser()
  const kind = tenant?.kind ?? null
  // A packhouse answer needs no profile; a farm answer waits for the seat.
  const settled = !tenantPending && (kind !== 'FARM' || !mePending)
  return { allowed: settled && askAvailableFor(kind, tenantRoleOf(me)), settled, kind }
}
